import React, { memo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

interface BulkActionButtonBarProps {
  selectedCount: number;
  onActionClick: () => void;
}

/**
 * Bulk-action bar for the Event Delivery page.
 *
 * Renders a floating bar that becomes visible when one or more grid rows are
 * selected. Displays the selected row count and a single "Update Delivery
 * Method" button that triggers the bulk-update dialog.
 *
 * @param {number}   selectedCount - Number of currently selected grid rows.
 * @param {Function} onActionClick - Callback invoked when the action button is clicked.
 * @returns {JSX.Element|null} The bar element, or null when no rows are selected.
 */
const BulkActionButtonBar = ({ selectedCount, onActionClick }: BulkActionButtonBarProps) => {
  // If no rows are selected, hide this bar completely to reduce UI clutter
  if (selectedCount === 0) return null;

  return (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1.25,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1a202c' : '#f8fafc',
            border: '1px solid',
            borderColor: (theme) => theme.palette.mode === 'dark' ? '#2d3748' : '#e2e8f0',
            borderRadius: '12px',
            boxShadow: '0 2px 8px -2px rgba(0,0,0,0.05)',
            mb: 2,
            // Add smooth fade-in effect when user selects rows
            animation: 'fadeIn 0.25s ease-in-out',
            '@keyframes fadeIn': {
                from: { opacity: 0, transform: 'clientY(-10px)' },
                to: { opacity: 1, transform: 'clientY(0)' }
            }
        }}
    >
        {/* left: display selected count */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: 'primary.main',
                }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                Selected <span style={{ color: '#1976d2', fontWeight: 700 }}>{selectedCount}</span> item{selectedCount > 1 ? 's' : ''}
            </Typography>
        </Box>

        {/* right: action button */}
        <Button
            variant="contained"
            size="small"
            startIcon={<EditOutlinedIcon sx={{ fontSize: '16px !important' }} />}
            onClick={onActionClick}
            sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                px: 2,
                py: 0.75,
                borderRadius: '8px',
                boxShadow: 'none',
                backgroundColor: 'primary.main',
                '&:hover': {
                    backgroundColor: 'primary.dark',
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.2)',
                }
            }}
        >
            Update Delivery Method
        </Button>
    </Box>
  );
};
export default memo(BulkActionButtonBar);
